import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchWithAuth } from '../services/api';

const money = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n)) ? '-' : `$${Number(n).toFixed(2)}`;

const GL_OPTIONS = [
  { value: 'maintenance materials', label: '6152 Maintenance Materials' },
  { value: 'turnover materials', label: '6151 Turnover Materials' },
  { value: 'buildings', label: '1710 Buildings' }
];

const getDefaultGlForProperty = (property, propertyGlDefaults, amount = 0) => {
  if (!property || property === 'UNKNOWN') {
    return 'maintenance materials';
  }
  if (Number(amount) > 2500) {
    return 'buildings';
  }
  return propertyGlDefaults[property] || 'maintenance materials';
};

const slimInvoiceForSubmit = (invoice) => {
  const header = invoice.header || {};
  return {
    header: {
      sale_date: header.sale_date,
      due_date: header.due_date,
      total: header.total,
      total_outstanding: header.total_outstanding,
      subtotal: header.subtotal
    },
    allocated_property: invoice.allocated_property,
    default_gl_label: invoice.default_gl_label
  };
};

const downloadCsv = (contents, filename) => {
  const blob = new Blob([contents], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.style.display = 'none';
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(link);
};

const getConfidenceColor = (score) => {
  if (score >= 85) return 'text-green-700';
  if (score >= 60) return 'text-yellow-700';
  return 'text-orange-700';
};

const getInvoiceConfidence = (invoice) => {
  const score = Number(invoice.confidence_score);
  if (invoice.allocated_property === 'UNKNOWN') {
    return Number.isFinite(score) ? Math.min(score, 0) : 0;
  }
  return Number.isFinite(score) ? score : 0;
};

const InvoiceSummaryTable = ({ invoices, onSelectInvoice, onPropertyChange, activeInvoiceId }) => {
  const sorted = useMemo(() => {
    return [...invoices].sort((a, b) => {
      const diff = getInvoiceConfidence(a) - getInvoiceConfidence(b);
      if (diff !== 0) return diff;
      const invA = a.header?.invoice_number || '';
      const invB = b.header?.invoice_number || '';
      return invA.localeCompare(invB);
    });
  }, [invoices]);

  const needsReviewCount = sorted.filter((inv) => getInvoiceConfidence(inv) < 85).length;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <h3 className="text-lg font-semibold">Invoice summary</h3>
        <p className="text-sm text-gray-600">
          Sorted by lowest confidence first ({sorted.length} invoices
          {needsReviewCount > 0 ? `, ${needsReviewCount} below 85%` : ''}). Click invoice or PO to
          jump; change property in the table.
        </p>
      </div>
      <div className="overflow-x-auto max-h-96 overflow-y-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left sticky top-0 z-10">
            <tr>
              <th className="px-4 py-2 font-medium text-gray-700">Invoice</th>
              <th className="px-4 py-2 font-medium text-gray-700">PO (raw)</th>
              <th className="px-4 py-2 font-medium text-gray-700">Property (selected)</th>
              <th className="px-4 py-2 font-medium text-gray-700 text-right">Total</th>
              <th className="px-4 py-2 font-medium text-gray-700 text-right">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((inv) => {
              const confidence = getInvoiceConfidence(inv);
              const invoiceLabel = inv.header?.invoice_number || inv.id || '-';
              const po = inv.header?.raw_po || '-';
              const property = inv.allocated_property || 'UNKNOWN';
              const isActive = activeInvoiceId === inv.id;
              const needsReview = confidence < 85 || property === 'UNKNOWN';

              return (
                <tr
                  key={inv.id}
                  className={`border-t transition-colors ${
                    isActive ? 'bg-blue-50' : needsReview ? 'hover:bg-amber-50/50' : 'hover:bg-gray-50/50'
                  }`}
                >
                  <td
                    onClick={() => onSelectInvoice(inv.id)}
                    className="px-4 py-2 font-medium whitespace-nowrap cursor-pointer"
                  >
                    {invoiceLabel}
                  </td>
                  <td
                    onClick={() => onSelectInvoice(inv.id)}
                    className="px-4 py-2 max-w-xs truncate cursor-pointer"
                    title={po}
                  >
                    {po}
                  </td>
                  <td className="px-4 py-2 min-w-[12rem]" onClick={(e) => e.stopPropagation()}>
                    <PropertySelector
                      invoice={inv}
                      onPropertyChange={onPropertyChange}
                      compact
                    />
                  </td>
                  <td
                    onClick={() => onSelectInvoice(inv.id)}
                    className="px-4 py-2 text-right font-medium whitespace-nowrap cursor-pointer"
                  >
                    {money(inv.amount)}
                  </td>
                  <td
                    onClick={() => onSelectInvoice(inv.id)}
                    className={`px-4 py-2 text-right font-medium cursor-pointer ${getConfidenceColor(confidence)}`}
                  >
                    {confidence.toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PropertySelector = ({ invoice, onPropertyChange, compact = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !compact || !buttonRef.current) {
      setDropdownStyle(null);
      return;
    }

    const updatePosition = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(rect.width, 280),
        zIndex: 50
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, compact]);

  const filteredMatches = (invoice.property_matches || []).filter((match) => {
    const address = (match.address || '').toLowerCase();
    const owner = (match.owner || '').toLowerCase();
    const query = searchTerm.toLowerCase();
    const matchesSearch = address.includes(query) || owner.includes(query);
    return searchTerm.length > 0 ? matchesSearch : matchesSearch && Number(match.score || 0) > 50;
  });

  const chooseProperty = (address, owner, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    onPropertyChange(invoice.id, address, owner || 'UNKNOWN');
    setIsOpen(false);
    setSearchTerm('');
  };

  const dropdownPanel = isOpen && (!compact || dropdownStyle) ? (
    <div
      className={`bg-white border rounded-md shadow-lg max-h-64 overflow-hidden ${
        compact ? '' : 'absolute z-10 w-full mt-1'
      }`}
      style={compact ? dropdownStyle || undefined : undefined}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="p-2 border-b">
        <input
          type="text"
          placeholder="Search properties..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-2 border rounded text-sm"
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      <div className="max-h-48 overflow-y-auto overscroll-contain">
        {filteredMatches.map((match, idx) => (
          <button
            key={`${match.address}-${idx}`}
            onClick={(e) => chooseProperty(match.address, match.owner, e)}
            className="w-full text-left px-3 py-2 hover:bg-gray-100 border-b"
          >
            <div className="font-medium">{match.address}</div>
            <div className="text-sm text-gray-600">{match.owner}</div>
            <div className={`text-xs ${getConfidenceColor(Number(match.score || 0))}`}>
              {Number(match.score || 0).toFixed(1)}% confidence
            </div>
          </button>
        ))}
        {filteredMatches.length === 0 && searchTerm && (
          <div className="px-3 py-2 text-gray-500 text-sm">No matching properties found</div>
        )}

        <button
          onClick={(e) => chooseProperty('UNKNOWN', 'UNKNOWN', e)}
          className="w-full text-left px-3 py-2 hover:bg-gray-100 border-t bg-gray-50"
        >
          <div className="font-medium text-gray-700">Unknown</div>
          <div className="text-sm text-gray-500">Deselect property</div>
        </button>
      </div>
    </div>
  ) : null;

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((open) => !open);
        }}
        className={`w-full text-left border rounded hover:bg-gray-50 flex justify-between items-center gap-2 ${
          compact ? 'p-1 text-xs' : 'p-2'
        } ${invoice.allocated_property === 'UNKNOWN' ? 'text-orange-700' : ''}`}
      >
        <span className="truncate">
          {invoice.allocated_property === 'UNKNOWN' ? 'Select Property' : invoice.allocated_property}
        </span>
        <svg className={`${compact ? 'w-3 h-3' : 'w-4 h-4'} shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {dropdownPanel}
    </div>
  );
};

const AllocationSummary = ({
  groupedAllocations,
  glAccounts,
  propertyGlDefaults,
  onGLAccountChange,
  onExport,
  exportLoading,
  onPost,
  postLoading,
  canPost
}) => {
  if (!groupedAllocations.order.length) return null;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b flex justify-between items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold">Allocation Summary</h3>
          <p className="text-sm text-gray-600">Grouped by owner and selected property</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={onExport}
            disabled={exportLoading || postLoading || groupedAllocations.exportableCount === 0}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400 text-sm"
          >
            {exportLoading ? 'Exporting...' : 'Export Bill'}
          </button>
          <button
            onClick={onPost}
            disabled={postLoading || exportLoading || !canPost || groupedAllocations.exportableCount === 0}
            className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 disabled:bg-gray-400 text-sm"
          >
            {postLoading ? 'Posting...' : 'Post to AppFolio'}
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {groupedAllocations.order.map(({ owner, properties }) => {
          const ownerSubtotal = properties.reduce((sum, item) => sum + item.amount, 0);
          return (
            <div key={owner} className="border-b pb-3 last:border-b-0">
              <div className="font-semibold text-gray-800 mb-2 flex justify-between items-center">
                <span>{owner}</span>
                <span className="text-blue-600 font-bold">{money(ownerSubtotal)}</span>
              </div>
              {properties.map(({ property, amount }) => {
                const isUnknown = property === 'UNKNOWN';
                return (
                  <div key={`${owner}-${property}`} className="flex justify-between items-center text-sm pl-4 py-1 gap-3">
                    <div className="flex-1">
                      <span className={`${isUnknown ? 'text-orange-700' : 'text-gray-600'}`}>{property}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="font-medium">{money(amount)}</span>
                      {!isUnknown ? (
                        <select
                          value={glAccounts[property] || getDefaultGlForProperty(property, propertyGlDefaults, amount)}
                          onChange={(e) => onGLAccountChange(property, e.target.value)}
                          className="text-xs p-1 border rounded"
                        >
                          {GL_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-xs text-orange-700">Not exportable</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}

        <div className="pt-4 border-t flex justify-between font-bold">
          <span>Total</span>
          <span>{money(groupedAllocations.totalAmount)}</span>
        </div>
      </div>
    </div>
  );
};

const LcaExpensesPage = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const [error, setError] = useState(null);
  const [documentWarnings, setDocumentWarnings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [propertyGlDefaults, setPropertyGlDefaults] = useState({});
  const [glAccounts, setGlAccounts] = useState({});
  const [manualGlOverrides, setManualGlOverrides] = useState(new Set());
  const [activeInvoiceId, setActiveInvoiceId] = useState(null);
  const invoiceRefs = useRef({});

  const groupedAllocations = useMemo(() => {
    const grouped = {};
    let totalAmount = 0;
    let exportableCount = 0;

    invoices.forEach((invoice) => {
      const owner = invoice.allocated_owner || 'UNKNOWN';
      const property = invoice.allocated_property || 'UNKNOWN';
      const amount = Number(invoice.amount || 0);
      totalAmount += amount;
      if (property !== 'UNKNOWN') {
        exportableCount += 1;
      }
      if (!grouped[owner]) {
        grouped[owner] = {};
      }
      grouped[owner][property] = (grouped[owner][property] || 0) + amount;
    });

    return {
      totalAmount,
      exportableCount,
      order: Object.entries(grouped).map(([owner, properties]) => ({
        owner,
        properties: Object.entries(properties).map(([property, amount]) => ({ property, amount }))
      }))
    };
  }, [invoices]);

  useEffect(() => {
    if (!groupedAllocations.order.length) return;

    let hasChanges = false;
    const nextAccounts = { ...glAccounts };

    groupedAllocations.order.forEach(({ properties }) => {
      properties.forEach(({ property, amount }) => {
        if (property === 'UNKNOWN' || manualGlOverrides.has(property)) {
          return;
        }
        const nextValue = getDefaultGlForProperty(property, propertyGlDefaults, amount);
        if (nextAccounts[property] !== nextValue) {
          nextAccounts[property] = nextValue;
          hasChanges = true;
        }
      });
    });

    if (hasChanges) {
      setGlAccounts(nextAccounts);
    }
  }, [groupedAllocations, manualGlOverrides, glAccounts, propertyGlDefaults]);

  const handleParse = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Please choose a PDF file.');
      return;
    }

    setLoading(true);
    setError(null);
    setDocumentWarnings([]);
    setInvoices([]);
    setPropertyGlDefaults({});
    setGlAccounts({});
    setManualGlOverrides(new Set());
    setActiveInvoiceId(null);
    setSubmitResult(null);
    invoiceRefs.current = {};

    try {
      const formData = new FormData();
      formData.append('pdf', file);
      const response = await fetchWithAuth('/api/expenses/lca/parse', {
        method: 'POST',
        body: formData
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Parse failed');
      }
      setInvoices(result.invoices || []);
      setPropertyGlDefaults(result.gl_defaults_by_property || {});
      setDocumentWarnings(result.warnings || []);
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePropertyChange = useCallback((invoiceId, propertyAddress, owner) => {
    setInvoices((current) =>
      current.map((invoice) =>
        invoice.id === invoiceId
          ? {
              ...invoice,
              allocated_property: propertyAddress,
              allocated_owner: owner,
              default_gl_label:
                propertyAddress && propertyAddress !== 'UNKNOWN'
                  ? getDefaultGlForProperty(propertyAddress, propertyGlDefaults)
                  : null,
              manually_assigned: true
            }
          : invoice
      )
    );
  }, [propertyGlDefaults]);

  const handleGlAccountChange = useCallback((property, value) => {
    setManualGlOverrides((prev) => new Set([...prev, property]));
    setGlAccounts((prev) => ({ ...prev, [property]: value }));
  }, []);

  const scrollToInvoice = useCallback((invoiceId) => {
    setActiveInvoiceId(invoiceId);
    const node = invoiceRefs.current[invoiceId];
    if (node) {
      node.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
  }, []);

  const setInvoiceRef = useCallback((invoiceId, node) => {
    if (node) {
      invoiceRefs.current[invoiceId] = node;
    } else {
      delete invoiceRefs.current[invoiceId];
    }
  }, []);

  const handlePostToAppFolio = async () => {
    if (!file) {
      setError('Upload the invoice PDF before posting to AppFolio.');
      return;
    }

    try {
      setPostLoading(true);
      setError(null);
      setSubmitResult(null);
      const formData = new FormData();
      formData.append('pdf', file);
      formData.append(
        'reviewed',
        JSON.stringify({
          invoices: invoices.map(slimInvoiceForSubmit),
          glAccounts
        })
      );
      const response = await fetchWithAuth('/api/expenses/lca/submit', {
        method: 'POST',
        body: formData
      });
      if (!response) {
        throw new Error('Session expired — please sign in again.');
      }
      let result;
      try {
        result = await response.json();
      } catch {
        if (response.status === 413) {
          throw new Error(
            'Upload too large for the server. Try a smaller PDF or contact support.'
          );
        }
        throw new Error(`Post failed (HTTP ${response.status})`);
      }
      if (result.status === 'failed') {
        setSubmitResult(result);
        throw new Error(
          result.stage ? `Failed at ${result.stage}: ${result.error}` : result.error
        );
      }
      if (!response.ok) {
        throw new Error(result.error || 'Failed to post bill');
      }
      setSubmitResult(result);
    } catch (err) {
      setError(err.message || 'Post to AppFolio failed');
    } finally {
      setPostLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const response = await fetchWithAuth('/api/expenses/lca/export-bill', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ invoices, glAccounts })
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to export bill');
      }
      downloadCsv(result.bill_csv_data, result.bill_filename);
      alert(
        `Bill exported successfully!\nBill: ${result.bill_filename}\nProperties: ${result.total_properties}\nTotal Amount: $${Number(result.total_amount || 0).toFixed(2)}`
      );
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Lowe&apos;s Commercial Account (LCA)</h3>
        <p className="text-sm text-gray-600 mb-4">
          Upload a Lowe&apos;s commercial invoice PDF. Review the parsed invoices, override
          properties if needed, pick GL accounts, then export a CSV or post directly to AppFolio.
        </p>
        <form onSubmit={handleParse} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Invoice PDF</label>
            <input
              type="file"
              accept=".pdf,application/pdf"
              onChange={(ev) => setFile(ev.target.files?.[0] || null)}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>
          <button
            type="submit"
            disabled={loading || !file}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400"
          >
            {loading ? 'Parsing...' : 'Parse PDF'}
          </button>
        </form>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {submitResult && (
        <div
          className={`border px-4 py-3 rounded-lg text-sm ${
            submitResult.status === 'posted'
              ? 'bg-green-50 border-green-200 text-green-900'
              : submitResult.status === 'skipped'
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {submitResult.status === 'posted' && (
            <p>
              Posted bill to AppFolio (reference: {submitResult.reference}, bill id:{' '}
              {submitResult.bill_id}
              {submitResult.attachment_attached === false ? ', PDF attach failed' : ''}).
            </p>
          )}
          {submitResult.status === 'skipped' && (
            <p>Skipped: {submitResult.reason} (reference: {submitResult.reference})</p>
          )}
          {submitResult.status === 'failed' && (
            <div className="space-y-2">
              <p>
                Failed at {submitResult.stage}: {submitResult.error}
              </p>
              {submitResult.payload && (
                <details className="text-xs">
                  <summary className="cursor-pointer font-medium">Request payload sent to AppFolio</summary>
                  <pre className="mt-2 p-2 bg-white/50 rounded overflow-auto max-h-64">
                    {JSON.stringify(submitResult.payload, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      )}

      {documentWarnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg">
          <div className="font-semibold mb-1">Document warnings</div>
          <ul className="list-disc list-inside text-sm space-y-1">
            {documentWarnings.map((warning, idx) => (
              <li key={idx}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      {invoices.length > 0 && (
        <>
          <AllocationSummary
            groupedAllocations={groupedAllocations}
            glAccounts={glAccounts}
            propertyGlDefaults={propertyGlDefaults}
            onGLAccountChange={handleGlAccountChange}
            onExport={handleExport}
            exportLoading={exportLoading}
            onPost={handlePostToAppFolio}
            postLoading={postLoading}
            canPost={Boolean(file)}
          />

          <InvoiceSummaryTable
            invoices={invoices}
            onSelectInvoice={scrollToInvoice}
            onPropertyChange={handlePropertyChange}
            activeInvoiceId={activeInvoiceId}
          />

          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Invoice details</h3>
            <div className="space-y-6">
              {invoices.map((inv, idx) => (
                <div
                  key={inv.id || idx}
                  ref={(node) => setInvoiceRef(inv.id, node)}
                  className={`bg-white p-6 rounded-lg shadow space-y-4 scroll-mt-6 transition-shadow ${
                    activeInvoiceId === inv.id ? 'ring-2 ring-blue-400' : ''
                  }`}
                >
          <div className="flex flex-wrap justify-between gap-2 border-b pb-3">
            <h4 className="text-md font-semibold text-gray-800">
              {inv.header?.invoice_number ? `Invoice ${inv.header.invoice_number}` : `Invoice ${idx + 1}`}
            </h4>
            <div className="text-right">
              <div
                className={`text-sm font-medium ${
                  inv.reconciliation_status === 'ok' ? 'text-green-700' : 'text-orange-700'
                }`}
              >
                Reconciliation: {inv.reconciliation_status}
              </div>
              <div className={`text-sm ${getConfidenceColor(Number(inv.confidence_score || 0))}`}>
                Match confidence: {Number(inv.confidence_score || 0).toFixed(1)}%
              </div>
            </div>
          </div>

          {inv.warnings?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-900 px-3 py-2 rounded text-sm">
              <div className="font-semibold mb-1">Warnings</div>
              <ul className="list-disc list-inside space-y-1">
                {inv.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-500">Invoice #</span>{' '}
              <span className="font-medium">{inv.header.invoice_number || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">Account</span>{' '}
              <span className="font-medium">{inv.header.account_number || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">PO (raw)</span>{' '}
              <span className="font-medium break-all">{inv.header.raw_po || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">Owner</span>{' '}
              <span className="font-medium">{inv.allocated_owner || 'UNKNOWN'}</span>
            </div>
            <div className="md:col-span-2">
              <div className="text-gray-500 mb-1">Property</div>
              <PropertySelector invoice={inv} onPropertyChange={handlePropertyChange} />
              {inv.manually_assigned && <div className="text-xs text-blue-700 mt-1">Manual override</div>}
            </div>
            <div>
              <span className="text-gray-500">Sale date</span>{' '}
              <span className="font-medium">{inv.header.sale_date || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">Due date</span>{' '}
              <span className="font-medium">{inv.header.due_date || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">Store</span>{' '}
              <span className="font-medium">{inv.header.store_number || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">Buyer</span>{' '}
              <span className="font-medium">{inv.header.buyer_name || '-'}</span>
            </div>
            <div>
              <span className="text-gray-500">Subtotal</span>{' '}
              <span className="font-medium">{money(inv.header.subtotal)}</span>
            </div>
            <div>
              <span className="text-gray-500">Tax</span>{' '}
              <span className="font-medium">{money(inv.header.tax)}</span>
            </div>
            <div>
              <span className="text-gray-500">Total / outstanding</span>{' '}
              <span className="font-medium">
                {money(inv.header.total)} / {money(inv.header.total_outstanding)}
              </span>
            </div>
          </div>

          <div>
            <h5 className="text-sm font-semibold text-gray-700 mb-2">Line items</h5>
            <div className="overflow-x-auto border rounded">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-2 py-2">SKU</th>
                    <th className="px-2 py-2">Description</th>
                    <th className="px-2 py-2">Qty</th>
                    <th className="px-2 py-2">Unit price</th>
                    <th className="px-2 py-2">Item total</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.line_items
                    .filter((li) => li.line_type !== 'sales_tax' && !li.parse_error)
                    .map((li, r) => (
                      <tr key={r} className="border-t">
                        <td className="px-2 py-1 whitespace-nowrap">{li.sku || '-'}</td>
                        <td className="px-2 py-1">{li.description || '-'}</td>
                        <td className="px-2 py-1">{li.qty ?? '-'}</td>
                        <td className="px-2 py-1">{money(li.unit_price)}</td>
                        <td className="px-2 py-1">{money(li.item_total)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            {inv.line_items.some((li) => li.parse_error) && (
              <div className="mt-3 border border-orange-200 rounded p-2 bg-orange-50 text-sm">
                <div className="font-semibold text-orange-900 mb-1">Rows not fully parsed</div>
                <ul className="space-y-1 list-disc list-inside text-orange-900">
                  {inv.line_items
                    .filter((li) => li.parse_error)
                    .map((li, i) => (
                      <li key={i}>
                        <span className="font-mono text-xs">{li.raw_line}</span>
                        <span className="text-orange-800"> - {li.parse_error}</span>
                      </li>
                    ))}
                </ul>
              </div>
            )}
          </div>

                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LcaExpensesPage;
